import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const ProductCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 12px;
`;

const ProductName = styled.h3`
  font-size: 18px;
  margin: 0 0 8px 0;
  text-align: center;
`;

const ProductPrice = styled.p`
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 12px 0;
`;

const ViewButton = styled(Link)`
  background-color: #4CAF50;
  color: white;
  padding: 8px 16px;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #45a049;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
`;
const Header = styled.h2`
  font-size: 24px;
  color: #FF6347;
  text-align: center;
  margin-bottom: 20px;
`;


const ShowAllButton = styled.button`
  background-color: #008CBA;
  color: white;
  border: none;
  padding: 10px 20px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  margin: 4px 2px;
  transition-duration: 0.4s;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: #005f7f;
  }
`;

const ProductContainer = ({ categoryId, showFeatured = false }) => {
  const [products, setProducts] = useState([]);
  const [visibleProducts, setVisibleProducts] = useState(8);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        let url = '/api/products/';
        if (showFeatured) {
          url = '/api/products/featured/';
        } else if (categoryId) {
          url += `?category=${categoryId}`;
        }
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setProducts(data);
        setLoading(false);
      } catch (e) {
        setError('Failed to fetch products');
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryId, showFeatured]);

  const showAllProducts = () => {
    setVisibleProducts(products.length);
  };

  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return price.toFixed(2);
    }
    if (typeof price === 'string') {
      return parseFloat(price).toFixed(2);
    }
    return 'N/A';
  };

  if (loading) return <p>Loading products...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div>
       <Header>Our Popular Products</Header>
      <ProductGrid>
        {products.slice(0, visibleProducts).map((product) => (
          <ProductCard key={product.id}>
            {product.image && <ProductImage src={product.image} alt={product.name || 'Product'} />}
            <ProductName>{product.name || 'Unnamed Product'}</ProductName>
            <ProductPrice>${formatPrice(product.price)}</ProductPrice>
            {product.slug && (
              <ViewButton to={`/product/${product.slug}`}>View Product</ViewButton>
            )}
          </ProductCard>
        ))}
      </ProductGrid>
      {visibleProducts < products.length && (
        <ShowAllButton onClick={showAllProducts}>Show All Products</ShowAllButton>
      )}
    </div>
  );
};

export default ProductContainer;